import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import logo from '../../logo.png';
import { Star, X, ChevronRight, Award, ClipboardList, Tag } from 'lucide-react';

export default function TalentoAvanzado() {
  const navigate = useNavigate();
  const [perfiles, setPerfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [perfilSeleccionado, setPerfilSeleccionado] = useState(null);
  const [mostrarDetalle, setMostrarDetalle] = useState(false);
  const [tabDetalle, setTabDetalle] = useState('info');
  const [mensaje, setMensaje] = useState(null);

  const [formSeguimiento, setFormSeguimiento] = useState({
    accion: '', resultado: '', observaciones: ''
  });

  const [formEvaluacion, setFormEvaluacion] = useState({
    score_tecnico: 0, score_territorial: 0, score_liderazgo: 0,
    score_confianza: 0, score_gobierno: 0, recomendacion: '', comentario: ''
  });

  const [formClasificacion, setFormClasificacion] = useState({
    clasificacion_principal: '', clasificacion_secundaria: ''
  });

  const [filtroEstatus, setFiltroEstatus] = useState('');

  useEffect(() => {
    fetchPerfiles();
  }, []);

  const fetchPerfiles = async () => {
    try {
      setLoading(true);
      const res = await api.get('/talento');
      setPerfiles(res.data.perfiles || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerDetalle = async (perfil) => {
    try {
      const res = await api.get(`/talento/${perfil.id}`);
      setPerfilSeleccionado(res.data);
      setTabDetalle('info');
      setMostrarDetalle(true);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleActualizarEstatus = async (nuevoEstatus) => {
    try {
      await api.put(`/talento/${perfilSeleccionado.perfil.id}/estatus`, { estatus_talento: nuevoEstatus });
      setMensaje({ tipo: 'exito', texto: 'Estatus actualizado.' });
      const res = await api.get(`/talento/${perfilSeleccionado.perfil.id}`);
      setPerfilSeleccionado(res.data);
      fetchPerfiles();
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error al actualizar.' });
    }
  };

  const handleGuardarSeguimiento = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/talento/${perfilSeleccionado.perfil.id}/seguimiento`, formSeguimiento);
      setMensaje({ tipo: 'exito', texto: 'Seguimiento agregado.' });
      setFormSeguimiento({ accion: '', resultado: '', observaciones: '' });
      const res = await api.get(`/talento/${perfilSeleccionado.perfil.id}`);
      setPerfilSeleccionado(res.data);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error al guardar.' });
    }
  };

  const handleGuardarEvaluacion = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/talento/${perfilSeleccionado.perfil.id}/evaluaciones`, formEvaluacion);
      setMensaje({ tipo: 'exito', texto: 'Evaluación guardada.' });
      setFormEvaluacion({ score_tecnico: 0, score_territorial: 0, score_liderazgo: 0, score_confianza: 0, score_gobierno: 0, recomendacion: '', comentario: '' });
      const res = await api.get(`/talento/${perfilSeleccionado.perfil.id}`);
      setPerfilSeleccionado(res.data);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error al guardar.' });
    }
  };

  const handleGuardarClasificacion = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/talento/${perfilSeleccionado.perfil.id}/clasificacion`, formClasificacion);
      setMensaje({ tipo: 'exito', texto: 'Clasificación guardada.' });
      const res = await api.get(`/talento/${perfilSeleccionado.perfil.id}`);
      setPerfilSeleccionado(res.data);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error al guardar.' });
    }
  };

  const getEstatusColor = (estatus) => {
    switch (estatus) {
      case 'basico': return 'bg-gray-100 text-gray-700';
      case 'por_ampliar': return 'bg-yellow-100 text-yellow-700';
      case 'evaluado': return 'bg-blue-100 text-blue-700';
      case 'estrategico': return 'bg-purple-100 text-purple-700';
      case 'descartado': return 'bg-red-100 text-red-700';
      case 'reserva': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const scorePromedio = (eval_) => {
    const scores = [eval_.score_tecnico, eval_.score_territorial, eval_.score_liderazgo, eval_.score_confianza, eval_.score_gobierno].filter(s => s !== null);
    if (scores.length === 0) return 0;
    return (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1);
  };

  const perfilesFiltrados = filtroEstatus ? perfiles.filter(p => p.estatus_talento === filtroEstatus) : perfiles;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <p className="text-gray-500">Cargando perfiles de talento...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">

      <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg"
        style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
        <img src={logo} alt="DOMINIUM" className="h-16" />
        <button onClick={() => navigate('/dashboard')}
          className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm transition">
          ← Volver al Dashboard
        </button>
      </header>

      <main className="p-6 max-w-7xl mx-auto">

        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Star className="text-yellow-500" size={28} />
            Banco de Talento — Gestión Avanzada
          </h2>
          <p className="text-gray-500 text-sm">Seguimiento, evaluación y clasificación de perfiles</p>
        </div>

        {/* TARJETAS RESUMEN */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          {['basico', 'por_ampliar', 'evaluado', 'estrategico', 'reserva', 'descartado'].map(est => (
            <div key={est} className={`bg-white rounded-xl shadow-sm p-3 text-center cursor-pointer border-2 transition ${filtroEstatus === est ? 'border-green-500' : 'border-transparent'}`}
              onClick={() => setFiltroEstatus(filtroEstatus === est ? '' : est)}>
              <p className="text-xs text-gray-500 capitalize">{est.replace('_', ' ')}</p>
              <p className="text-xl font-bold text-gray-800">{perfiles.filter(p => p.estatus_talento === est).length}</p>
            </div>
          ))}
        </div>

        {/* LISTA DE PERFILES */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {perfilesFiltrados.length === 0 ? (
            <div className="col-span-3 bg-white rounded-xl shadow-sm p-12 text-center">
              <Star className="text-gray-300 mx-auto mb-3" size={48} />
              <p className="text-gray-500 font-medium">No hay perfiles de talento</p>
            </div>
          ) : perfilesFiltrados.map((p) => (
            <div key={p.id} className="bg-white rounded-xl shadow-sm p-5 border-t-4 border-yellow-400 hover:shadow-md transition cursor-pointer"
              onClick={() => handleVerDetalle(p)}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-gray-800">{p.nombre} {p.apellido_paterno}</h4>
                  <p className="text-xs text-gray-500 capitalize">{p.ocupacion_general}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEstatusColor(p.estatus_talento)}`}>
                  {p.estatus_talento?.replace('_', ' ')}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                <div className="bg-green-50 p-2 rounded">
                  <p className="text-gray-500">Disponibilidad</p>
                  <p className="font-medium capitalize">{p.disponibilidad_servir}</p>
                </div>
                <div className="bg-blue-50 p-2 rounded">
                  <p className="text-gray-500">Área</p>
                  <p className="font-medium">{p.area_interes || '—'}</p>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>📱 {p.telefono_principal}</span>
                <span className="flex items-center gap-1 text-green-600 font-medium">
                  Ver perfil <ChevronRight size={14} />
                </span>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-gray-400 text-xs mt-8">
          DOMINIUM © 2026 — Zacatlán, Puebla | GARCOR Design
        </p>
      </main>

      {/* MODAL DETALLE */}
      {mostrarDetalle && perfilSeleccionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between p-6"
              style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)', borderRadius: '1rem 1rem 0 0' }}>
              <div>
                <h3 className="text-white font-bold text-lg">
                  {perfilSeleccionado.perfil.nombre} {perfilSeleccionado.perfil.apellido_paterno}
                </h3>
                <p className="text-white text-opacity-80 text-sm capitalize">{perfilSeleccionado.perfil.ocupacion_general}</p>
              </div>
              <button onClick={() => { setMostrarDetalle(false); setMensaje(null); }}
                className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded transition">
                <X size={20} />
              </button>
            </div>

            {/* TABS */}
            <div className="flex border-b px-6">
              {[
                { key: 'info', label: '📋 Info', icon: ClipboardList },
                { key: 'seguimiento', label: '📌 Seguimiento', icon: ClipboardList },
                { key: 'evaluacion', label: '⭐ Evaluación', icon: Award },
                { key: 'clasificacion', label: '🏷 Clasificación', icon: Tag }
              ].map(t => (
                <button key={t.key} onClick={() => { setTabDetalle(t.key); setMensaje(null); }}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition ${
                    tabDetalle === t.key ? 'border-green-600 text-green-600' : 'border-transparent text-gray-600'
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>

            <div className="p-6">
              {mensaje && (
                <div className={`mb-4 p-3 rounded-lg text-sm ${
                  mensaje.tipo === 'exito' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'
                }`}>{mensaje.texto}</div>
              )}

              {/* TAB INFO */}
              {tabDetalle === 'info' && (
                <div>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">Teléfono</p>
                      <p className="font-medium">{perfilSeleccionado.perfil.telefono_principal}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">Sección</p>
                      <p className="font-medium">{perfilSeleccionado.perfil.numero_seccion || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">Área de Interés</p>
                      <p className="font-medium capitalize">{perfilSeleccionado.perfil.area_interes || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">Disponibilidad</p>
                      <p className="font-medium capitalize">{perfilSeleccionado.perfil.disponibilidad_servir || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">Tipo Participación</p>
                      <p className="font-medium capitalize">{perfilSeleccionado.perfil.tipo_participacion_interes?.replace('_', ' ') || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">Origen</p>
                      <p className="font-medium capitalize">{perfilSeleccionado.perfil.origen?.replace('_', ' ') || '—'}</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Cambiar Estatus:</p>
                    <div className="flex flex-wrap gap-2">
                      {['basico', 'por_ampliar', 'evaluado', 'estrategico', 'reserva', 'descartado'].map(est => (
                        <button key={est} onClick={() => handleActualizarEstatus(est)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                            perfilSeleccionado.perfil.estatus_talento === est
                              ? 'ring-2 ring-green-500 ' + getEstatusColor(est)
                              : getEstatusColor(est) + ' opacity-60 hover:opacity-100'
                          }`}>
                          {est.replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                  </div>

                  {perfilSeleccionado.clasificacion && (
                    <div className="bg-purple-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Clasificación actual</p>
                      <p className="font-medium text-purple-800">{perfilSeleccionado.clasificacion.clasificacion_principal}</p>
                      {perfilSeleccionado.clasificacion.clasificacion_secundaria && (
                        <p className="text-sm text-purple-600">{perfilSeleccionado.clasificacion.clasificacion_secundaria}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* TAB SEGUIMIENTO */}
              {tabDetalle === 'seguimiento' && (
                <div>
                  <form onSubmit={handleGuardarSeguimiento} className="bg-gray-50 p-4 rounded-lg mb-4 space-y-3">
                    <h5 className="font-semibold text-gray-700">Agregar Seguimiento</h5>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Acción realizada *</label>
                      <select value={formSeguimiento.accion} onChange={(e) => setFormSeguimiento({...formSeguimiento, accion: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" required>
                        <option value="">Seleccionar...</option>
                        <option value="llamada_telefonica">📞 Llamada telefónica</option>
                        <option value="entrevista">🤝 Entrevista</option>
                        <option value="solicitud_cv">📄 Solicitud de CV</option>
                        <option value="visita_domicilio">🏠 Visita a domicilio</option>
                        <option value="reunion_presencial">👥 Reunión presencial</option>
                        <option value="mensaje_whatsapp">📱 Mensaje WhatsApp</option>
                        <option value="evaluacion_aplicada">⭐ Evaluación aplicada</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Resultado</label>
                      <input value={formSeguimiento.resultado} onChange={(e) => setFormSeguimiento({...formSeguimiento, resultado: e.target.value})}
                        placeholder="Ej: Interesado, pendiente de confirmar..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Observaciones</label>
                      <textarea value={formSeguimiento.observaciones} onChange={(e) => setFormSeguimiento({...formSeguimiento, observaciones: e.target.value})}
                        rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                    <button type="submit"
                      className="w-full text-white font-semibold py-2 rounded-lg hover:opacity-90 transition text-sm"
                      style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                      Guardar Seguimiento
                    </button>
                  </form>

                  <h5 className="font-semibold text-gray-700 mb-2">Historial ({perfilSeleccionado.seguimiento.length})</h5>
                  {perfilSeleccionado.seguimiento.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-4">Sin seguimientos registrados</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {perfilSeleccionado.seguimiento.map((s, i) => (
                        <div key={i} className="bg-white border rounded-lg p-3">
                          <div className="flex justify-between items-start">
                            <p className="text-sm font-medium capitalize">{s.accion.replace('_', ' ')}</p>
                            <p className="text-xs text-gray-400">{new Date(s.fecha).toLocaleDateString('es-MX')}</p>
                          </div>
                          {s.resultado && <p className="text-xs text-gray-600 mt-1">✅ {s.resultado}</p>}
                          {s.observaciones && <p className="text-xs text-gray-500 mt-1">{s.observaciones}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* TAB EVALUACION */}
              {tabDetalle === 'evaluacion' && (
                <div>
                  <form onSubmit={handleGuardarEvaluacion} className="bg-gray-50 p-4 rounded-lg mb-4 space-y-3">
                    <h5 className="font-semibold text-gray-700">Nueva Evaluación (0-10)</h5>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { key: 'score_tecnico', label: '🔧 Técnico' },
                        { key: 'score_territorial', label: '🗺 Territorial' },
                        { key: 'score_liderazgo', label: '👑 Liderazgo' },
                        { key: 'score_confianza', label: '🤝 Confianza' },
                        { key: 'score_gobierno', label: '🏛 Gobierno' }
                      ].map(s => (
                        <div key={s.key}>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{s.label}</label>
                          <input type="number" min="0" max="10"
                            value={formEvaluacion[s.key]}
                            onChange={(e) => setFormEvaluacion({...formEvaluacion, [s.key]: parseInt(e.target.value) || 0})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                        </div>
                      ))}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Recomendación</label>
                      <select value={formEvaluacion.recomendacion} onChange={(e) => setFormEvaluacion({...formEvaluacion, recomendacion: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                        <option value="">Seleccionar...</option>
                        <option value="perfil_estrategico">⭐ Perfil estratégico</option>
                        <option value="seguir_cultivando">🌱 Seguir cultivando</option>
                        <option value="cargo_operativo">⚙️ Cargo operativo</option>
                        <option value="cargo_gobierno">🏛 Cargo de gobierno</option>
                        <option value="no_viable">❌ No viable</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Comentario</label>
                      <textarea value={formEvaluacion.comentario} onChange={(e) => setFormEvaluacion({...formEvaluacion, comentario: e.target.value})}
                        rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                    <button type="submit"
                      className="w-full text-white font-semibold py-2 rounded-lg hover:opacity-90 transition text-sm"
                      style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                      Guardar Evaluación
                    </button>
                  </form>

                  <h5 className="font-semibold text-gray-700 mb-2">Evaluaciones previas ({perfilSeleccionado.evaluaciones.length})</h5>
                  {perfilSeleccionado.evaluaciones.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-4">Sin evaluaciones registradas</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {perfilSeleccionado.evaluaciones.map((e, i) => (
                        <div key={i} className="bg-white border rounded-lg p-3">
                          <div className="flex justify-between items-start mb-2">
                            <p className="text-sm font-medium">Promedio: {scorePromedio(e)}/10</p>
                            <p className="text-xs text-gray-400">{new Date(e.fecha_evaluacion).toLocaleDateString('es-MX')}</p>
                          </div>
                          <div className="grid grid-cols-5 gap-1 text-xs text-center mb-2">
                            {[
                              { label: 'Téc', val: e.score_tecnico },
                              { label: 'Ter', val: e.score_territorial },
                              { label: 'Lid', val: e.score_liderazgo },
                              { label: 'Con', val: e.score_confianza },
                              { label: 'Gob', val: e.score_gobierno }
                            ].map((s, j) => (
                              <div key={j} className="bg-gray-50 rounded p-1">
                                <p className="text-gray-500">{s.label}</p>
                                <p className="font-bold">{s.val ?? '—'}</p>
                              </div>
                            ))}
                          </div>
                          {e.recomendacion && <p className="text-xs text-green-700 font-medium capitalize">{e.recomendacion.replace('_', ' ')}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* TAB CLASIFICACION */}
              {tabDetalle === 'clasificacion' && (
                <div>
                  {perfilSeleccionado.clasificacion && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                      <p className="text-xs text-gray-500 mb-1">Clasificación actual</p>
                      <p className="font-bold text-purple-800 text-lg">{perfilSeleccionado.clasificacion.clasificacion_principal}</p>
                      {perfilSeleccionado.clasificacion.clasificacion_secundaria && (
                        <p className="text-sm text-purple-600">{perfilSeleccionado.clasificacion.clasificacion_secundaria}</p>
                      )}
                    </div>
                  )}

                  <form onSubmit={handleGuardarClasificacion} className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <h5 className="font-semibold text-gray-700">Nueva Clasificación</h5>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Clasificación Principal *</label>
                      <select value={formClasificacion.clasificacion_principal} onChange={(e) => setFormClasificacion({...formClasificacion, clasificacion_principal: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" required>
                        <option value="">Seleccionar...</option>
                        <option value="cuadro_territorial">🗺 Cuadro territorial</option>
                        <option value="perfil_tecnico">🔧 Perfil técnico</option>
                        <option value="perfil_administrativo">📋 Perfil administrativo</option>
                        <option value="perfil_operativo">⚙️ Perfil operativo</option>
                        <option value="enlace_sectorial">🔗 Enlace sectorial</option>
                        <option value="liderazgo_social">👑 Liderazgo social</option>
                        <option value="perfil_gobierno_medio">🏛 Perfil de gobierno medio</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Clasificación Secundaria</label>
                      <input value={formClasificacion.clasificacion_secundaria} onChange={(e) => setFormClasificacion({...formClasificacion, clasificacion_secundaria: e.target.value})}
                        placeholder="Opcional..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                    <button type="submit"
                      className="w-full text-white font-semibold py-2 rounded-lg hover:opacity-90 transition text-sm"
                      style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                      Guardar Clasificación
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}